import { api } from '@/lib/api';

/** D16: register the service worker, request permission and persist a Push subscription.
 * Returns { ok, reason } — reason is a stable code for UI labels. */
export async function ensureBrowserPush(): Promise<{ ok: boolean; reason: string }> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };
  if (Notification.permission === 'default') {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return { ok: false, reason: perm === 'denied' ? 'denied' : 'dismissed' };
  }
  try {
    const status = await api.get<{ enabled: boolean; count: number; publicKey: string }>(
      '/push-subscriptions/status',
    );
    if (!status.enabled || !status.publicKey) return { ok: false, reason: 'server-disabled' };
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(status.publicKey).buffer as ArrayBuffer,
      });
    }
    await api.post('/push-subscriptions', {
      endpoint: sub.endpoint,
      p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('p256dh')!))),
      auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('auth')!))),
    });
    return { ok: true, reason: 'granted' };
  } catch (err) {
    return { ok: false, reason: 'error' };
  }
}

/** Remove the persisted subscription for this browser. */
export async function disableBrowserPush(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.delete(`/push-subscriptions?endpoint=${encodeURIComponent(sub.endpoint)}`);
        await sub.unsubscribe();
      }
    }
  } catch {
    /* best-effort unsubscribe */
  }
}

/** Convert a base64url VAPID public key into a Uint8Array for subscribe(). */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
